import { memo, useCallback, useState } from "react";
import { TenantDetailCardProps } from "./types";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import OibTextField from "ui/components/TextField/OibTextField/OibTextField";
import { EmailTextField } from "ui/components/TextField/EmailTextField";
import { WebTextField } from "ui/components/TextField/WebTextField";
import { MobilePhoneTextField } from "ui/components/TextField/MobilePhoneTextField";
import { PhoneTextField } from "ui/components/TextField/PhoneTextField";
import TenantEditCard from "ui/modules/tenants/components/TenantInfoEditCard/TenantEditCard";

const TenantDetailCard: React.FC<TenantDetailCardProps> = (props) => {
  const { tenant, onRefresh } = props;
  const { t } = useTranslate();

  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const handleEditClick = useCallback(() => {
    setShowEditModal(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowEditModal(false);
    onRefresh();
  }, [onRefresh]);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
  }, []);

  const isCompany = tenant.tenantType?.id === "C";
  const isPerson = tenant.tenantType?.id === "P";

  return (
    <>
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32">
        <div className="flex flex-col flex-1 max-w-[300px] ">
          <div>
            <p className="text-base font-bold text-slate-600 py-3">
              {t("admin.setting.tenant.title")}
            </p>
            <p className="text-sm">{t("admin.setting.tenant.description")}</p>
          </div>
        </div>
        <div className="flex justify-between items-center flex-1  px-3 my-3 ">
          <div className="flex flex-col min-w-[530px]">
            <div className="mb-3">
              <TextField
                type="text"
                name="tenantType"
                label={t("admin.setting.tenant.tenanttype")}
                placeholder={t("admin.setting.tenant.tenanttype.placeholder")}
                value={tenant.tenantType?.name}
                cssClassName="bg-white"
                disabled
              />
            </div>
            {!isPerson && (
              <div className="mb-3">
                <TextField
                  type="text"
                  name="companyName"
                  label={t("admin.setting.tenant.companyname")}
                  placeholder={t("admin.setting.tenant.companyname.placeholder")}
                  value={tenant.companyName}
                  cssClassName="bg-white"
                  disabled
                />
              </div>
            )}
            {!isCompany && (
              <div className="flex gap-4">
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="firstName"
                    label={t("admin.setting.tenant.firstname")}
                    placeholder={t("admin.setting.tenant.firstname.placeholder")}
                    value={tenant.firstName}
                    cssClassName="bg-white"
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="lastName"
                    label={t("admin.setting.tenant.lastname")}
                    placeholder={t("admin.setting.tenant.lastname.placeholder")}
                    value={tenant.lastName}
                    cssClassName="bg-white"
                    disabled
                  />
                </div>
              </div>
            )}
            {!isPerson && (
              <div className="mb-3">
                <TextField
                  type="text"
                  name="headquartersAddress"
                  label={t("admin.setting.tenant.headquartersaddress")}
                  placeholder={t(
                    "admin.setting.tenant.headquartersaddress.placeholder"
                  )}
                  value={tenant.headquartersAddress}
                  cssClassName="bg-white"
                  disabled
                />
              </div>
            )}
            <div className="flex gap-4 mb-3">
              <div className="flex-1">
                <PhoneTextField
                  type="text"
                  name="phoneNumber"
                  label={t("admin.setting.tenant.phonenumber")}
                  placeholder={t("admin.setting.tenant.phonenumber.placeholder")}
                  value={tenant.phoneNumber}
                  endIcon="/media/icons/phone.svg"
                  cssClassName="bg-white"
                  disabled
                />
              </div>
              <div className="flex-1">
                <MobilePhoneTextField
                  type="text"
                  name="mobilePhoneNumber"
                  label={t("admin.setting.tenant.mobilephonenumber")}
                  placeholder={t(
                    "admin.setting.tenant.mobilephonenumber.placeholder"
                  )}
                  value={tenant.mobilePhoneNumber}
                  cssClassName="bg-white"
                  disabled
                />
              </div>
            </div>
            <div className="flex gap-4 mb-3">
              <div className="flex-1">
                <EmailTextField
                  type="email"
                  name="email"
                  label={t("admin.setting.tenant.email")}
                  placeholder=""
                  value={tenant.email}
                  autoComplete="email"
                  cssClassName="bg-white"
                  disabled
                />
              </div>
              <div className="flex-1">
                <WebTextField
                  type="url"
                  name="web"
                  label={t("admin.setting.tenant.web")}
                  placeholder=""
                  value={tenant.web}
                  autoComplete="url"
                  cssClassName="bg-white"
                  disabled
                />
              </div>
            </div>
            <div className="mb-6">
              <OibTextField
                type="text"
                name="personalIdentificationNumber"
                label={t("admin.setting.tenant.personalidentificationnumber")}
                placeholder=""
                value={tenant.personalIdentificationNumber}
                cssClassName="bg-white"
                disabled
              />
            </div>
            <div className="mb-3">
              <TextField
                type="text"
                name="timezone"
                label={t("admin.setting.tenant.timezone")}
                placeholder={t("admin.setting.tenant.timezone.placeholder")}
                value={tenant.timezone}
                cssClassName="bg-white"
                disabled
              />
            </div>
          </div>
          <div>
            <Button
              variant="outlined"
              children={t("admin.edit")}
              onClick={handleEditClick}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </div>
      </div>

      <TenantEditCard
        tenantId={tenant.id}
        show={showEditModal}
        onSuccess={handleEditSuccess}
        onClose={handleEditClose}
          
      />
    </>
  );
};

export default memo(TenantDetailCard);
